/**
 * @fileoverview Atom: the small outline icon set the app draws on.
 * @module components/atoms/Icon
 */

import "./Icon.css"

/** Every icon the app can render. */
export type IconName =
	| "chevron-down"
	| "close"
	| "plus"
	| "minus"
	| "pencil"
	| "trash"
	| "check"
	| "home"
	| "chart"
	| "bell"
	| "flame"

/** Props accepted by {@link Icon}. */
export interface IconProps {
	/** Which glyph to draw. */
	readonly name: IconName
	/** Edge length in pixels. Defaults to 20. */
	readonly size?: number
}

/** Stroke path for each glyph, drawn on a 24×24 grid. */
const PATHS: Record<IconName, string> = {
	"chevron-down": "M5 8.5 12 15.5 19 8.5",
	close: "M6 6 18 18M18 6 6 18",
	plus: "M12 5v14M5 12h14",
	minus: "M5 12h14",
	pencil: "M15.5 4.5 19.5 8.5 8 20H4v-4ZM13.5 6.5 17.5 10.5",
	trash: "M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13",
	check: "M5 13 9.5 17.5 19 7",
	home: "M4 11.5 12 4l8 7.5M6 10v9a1 1 0 0 0 1 1h3v-6h4v6h3a1 1 0 0 0 1-1v-9",
	chart: "M5 20V11M12 20V4M19 20v-6",
	bell: "M12 3a5 5 0 0 0-5 5v3.2c0 .9-.35 1.77-.98 2.4L5 15h14l-1.02-1.4A3.4 3.4 0 0 1 17 11.2V8a5 5 0 0 0-5-5ZM10 18a2 2 0 0 0 4 0",
	flame: "M12 3c1 3-3 4.5-3 8a3 3 0 0 0 6 0c0-1-1-1.5-1-3 2 1.5 3 4 3 6a5 5 0 0 1-10 0c0-4 3-6 5-11Z",
}

/**
 * Outline icon, decorative by default.
 *
 * @param props - Which glyph to draw and its size.
 * @returns The rendered SVG. Pair it with a labelled control — it carries no
 *   accessible name of its own.
 *
 * @example
 * <Button ariaLabel="Supprimer"><Icon name="trash" /></Button>
 */
export function Icon({ name, size = 20 }: IconProps) {
	return (
		<svg
			className="icon"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d={PATHS[name]} />
		</svg>
	)
}
